import { cloneDeep } from 'lodash';
import { calcClientDerivatives } from '../calculations/calcClientDerivatives';
import {
  createPolicyFundingSourceFromQuote,
  createPolicyFundingSourceFromTraditionalLTCQuote,
} from '../calculations/roi/calcClientROIProjections';
import {
  getLtcPool,
  getTotalLtcCoveragePayout,
  getTotalOutOfPocketCost,
} from '../calculations/funding/projection/projectionTotals';
import {
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
} from '../calculations/funding/projection/sharedPolicyConfig';
import { CareEnvironment } from '../appModel';
import { type ConsumerQuote, isTraditionalConsumerQuote } from './consumerQuotes';

export interface ConsumerQuoteProjection {
  projection: ClaimProjection | null;
  totalCareCost: number;
  projectedPayout: number;
  outOfPocket: number;
  maxBenefitAtCareStart: number;
  monthlyMaxAtCareStart: { home: number; facility: number } | null;
  guaranteedDeathBenefitAfterCare: number;
  deathBenefitWithoutCare: number;
}

const PREVIEW_FUNDING_SOURCE_ID = 'consumer-plan-preview';

function policyFromQuote(quote: ConsumerQuote): PolicyFundingSource {
  return isTraditionalConsumerQuote(quote)
    ? createPolicyFundingSourceFromTraditionalLTCQuote(quote)
    : createPolicyFundingSourceFromQuote(quote);
}

/**
 * The monthly limit at home and in a facility in the year care starts: the engine's inflated monthly
 * cap for that year, scaled by each setting's share of the policy's highest monthly benefit.
 */
function monthlyMaxAtCareStart(
  policy: PolicyFundingSource,
  projection: ClaimProjection,
): ConsumerQuoteProjection['monthlyMaxAtCareStart'] {
  const inflatedCap = projection.yearSnapshots[0]?.monthlyBenefitCap ?? 0;
  const atIssueMax = getMaxMonthlyBenefitAmount(policy);
  if (!inflatedCap || !atIssueMax) return null;

  const atCareStart = (environment: CareEnvironment) =>
    (inflatedCap * getMonthlyBenefitAmountForEnvironment(policy, environment)) / atIssueMax;
  return { home: atCareStart(CareEnvironment.home), facility: atCareStart(CareEnvironment.fullCareFacility) };
}

/**
 * Runs one quote through the client's care plan as if it were their only policy, returning
 * the month-by-month claim projection and the totals the consumer flow shows. Falls back to the
 * quote's enriched coverage when a product isn't modeled by the projection engine.
 */
export function projectConsumerQuote(client: Client, quote: ConsumerQuote): ConsumerQuoteProjection {
  const clientWithPolicy = cloneDeep({ ...client, mutableClientPartner: null });
  const policy = policyFromQuote(quote);
  clientWithPolicy.multipleFundingSources.ltcPolicy = { [PREVIEW_FUNDING_SOURCE_ID]: policy };

  const derived = calcClientDerivatives(clientWithPolicy, null);
  const projection = derived.policyClaimProjections?.[PREVIEW_FUNDING_SOURCE_ID] ?? null;
  const hybrid = isTraditionalConsumerQuote(quote) ? null : quote;
  const deathBenefitWithoutCare = hybrid?.deathBenefitInitial ?? 0;
  const guaranteedDeathBenefitAfterCare = hybrid?.deathBenefitMinimumGuaranteedIndividual ?? 0;

  if (!projection?.months.length) {
    const totalCareCost = client.allPhaseCosts?.allPhaseInflatedTotalCost ?? 0;
    const projectedPayout = Math.min(totalCareCost, quote.projectedCostCoverage ?? 0);
    return {
      projection: null,
      totalCareCost,
      projectedPayout,
      outOfPocket: Math.max(0, totalCareCost - projectedPayout),
      maxBenefitAtCareStart: quote.maxAchievableCoverage ?? 0,
      monthlyMaxAtCareStart: null,
      guaranteedDeathBenefitAfterCare,
      deathBenefitWithoutCare,
    };
  }

  const projectedPayout = getTotalLtcCoveragePayout(projection.months);
  const outOfPocket = getTotalOutOfPocketCost(projection.months);

  return {
    projection,
    totalCareCost: projectedPayout + outOfPocket,
    projectedPayout,
    outOfPocket,
    // CareMatters pays its inflation rider beside a flat pool, so the pool alone leaves out most of
    // what the policy can pay by the time care starts; those products carry the full figure on terms.
    maxBenefitAtCareStart:
      projection.terms.lifetimeLtcBenefit ?? getLtcPool(projection.months[0].openState),
    monthlyMaxAtCareStart: monthlyMaxAtCareStart(policy, projection),
    guaranteedDeathBenefitAfterCare,
    deathBenefitWithoutCare,
  };
}
